import { Box, Typography } from "@mui/material";
import { AllPlans, SubscriptionTab } from "contexts/Subscription";
import {
  AddOnItemName,
  BannerCouponName,
  BannerPromoCode,
  Subscription,
  SubscriptionType,
  UpgradeDialogSource,
} from "schemas/dashboard";
import { Plans, getEquivalentPlansFromOverride } from "schemas/payments";

import { titleCase } from "utils/string";
import { formatEpochTimestamp } from "utils/time";

export const SubscriptionMessages: { [key in SubscriptionType]: JSX.Element } =
  {
    [SubscriptionType.CANCELED]: (
      <>
        ⚠️ Your subscription has been <strong>cancelled</strong>. To take
        advantages of all functionalities on this plan, please resubscribe.
      </>
    ),
    [SubscriptionType.PAUSED]: (
      <>
        ⚠️ Your subscription has been <strong>paused</strong>. To take
        advantages of all functionalities on this plan, please unpause.
      </>
    ),
    [SubscriptionType.INCOMPLETE_EXPIRED]: (
      <>
        ⚠️ Your payment was incomplete when subscribing to Bento. Please try
        again.
      </>
    ),
    [SubscriptionType.INCOMPLETE]: (
      <>
        ⚠️ Your payment was incomplete when subscribing to Bento. Please try
        again.
      </>
    ),
    [SubscriptionType.PAST_DUE]: (
      <>
        ⚠️ Your latest payment for the subscription{" "}
        <Box component="span" color="warning.main">
          <strong>failed</strong>
        </Box>
        . Please update your payment method using the manage button above.
      </>
    ),
    [SubscriptionType.UNPAID]: (
      <>
        ⚠️ Your latest payment for the subscription <strong>failed</strong>.
        Please update your payment method.
      </>
    ),
    [SubscriptionType.ACTIVE]: <></>,
    [SubscriptionType.TRIALING]: <></>,
  };

export const disabledCurrentPlan = (
  plan: Plans,
  subscription: Subscription | null,
) => {
  if (plan === Plans.FREE) return true;
  if (subscription?.overridePlanName) {
    return getEquivalentPlansFromOverride(
      subscription.overridePlanName,
    )?.includes(plan);
  }
  return false;
};

export enum PlanAction {
  upgrade = "Upgrade",
  resubscribe = "Resubscribe",
  manage = "Manage",
  renew = "Renew",
  retry = "Retry",
  downgrade = "Choose This Plan",
}
export const getCurrentPlanButton = (
  plan: Plans,
  userCurrentPlanName: Plans,
  subscription: Subscription | null,
) => {
  if (isCurrentPlan(subscription, plan)) {
    if (subscription?.subscription?.status === SubscriptionType.CANCELED) {
      return PlanAction.resubscribe;
    } else if (
      subscription?.subscription.cancelAt &&
      subscription?.subscription?.status === SubscriptionType.ACTIVE
    ) {
      return PlanAction.renew;
    } else if (
      subscription?.subscription?.status === SubscriptionType.INCOMPLETE ||
      subscription?.subscription?.status === SubscriptionType.INCOMPLETE_EXPIRED
    ) {
      return PlanAction.retry;
    } else {
      return PlanAction.manage;
    }
  } else return getOtherPlanButton(plan, userCurrentPlanName);
};

export const getOtherPlanButton = (tier: Plans, userCurrentPlan: Plans) => {
  const orders: { [key: string]: number } = {
    [Plans.FREE]: 0,
    [Plans.STARTER]: 1,
    [Plans.STARTER_YEARLY]: 1,
    [Plans.PRO]: 2,
    [Plans.PRO_YEARLY]: 2,
    [Plans.VA]: 3,
    [Plans.VA_YEARLY]: 3,
    [Plans.AUTOPILOT]: 3,
    [Plans.AUTOPILOT_YEARLY]: 3,
  };
  if (userCurrentPlan && orders[userCurrentPlan] >= orders[tier]) {
    return PlanAction.downgrade;
  } else {
    return PlanAction.upgrade;
  }
};

export const getStatusChipLabel = (status?: SubscriptionType) => {
  if (status === SubscriptionType.TRIALING) {
    return <strong>is active</strong>;
  } else if (
    status === SubscriptionType.INCOMPLETE ||
    status === SubscriptionType.INCOMPLETE_EXPIRED ||
    status === SubscriptionType.PAST_DUE ||
    status === SubscriptionType.UNPAID
  ) {
    return (
      <>
        has a <strong>failed payment</strong>. Please update your payment method
      </>
    );
  } else {
    return (
      <>
        is <strong>{titleCase(status || "")}</strong>
      </>
    );
  }
};

export const isCurrentPlan = (
  subscription: Subscription | null,
  plan: Plans,
) => {
  return (
    subscription?.subscription?.planName === plan ||
    (subscription?.subscription?.planName === Plans.STARTER_YEARLY &&
      plan === Plans.PRO_YEARLY) ||
    (subscription?.subscription?.planName === Plans.STARTER &&
      plan === Plans.PRO)
  );
};

export const isOnFreePlan = (subscription: Subscription | null) => {
  return (
    !subscription || !subscription.subscription || !subscription?.isSubscribed
  );
};

export const DisplayVaOnly = [
  UpgradeDialogSource.reply_score,
  UpgradeDialogSource.access_partnership_data,
  UpgradeDialogSource.va_metrics_sidebar,
  UpgradeDialogSource.upgrade_va_button,
  UpgradeDialogSource.upgrade_offer_dialog,
  UpgradeDialogSource.va_preview,
];

export const DisplayProAndVa = [
  UpgradeDialogSource.gamification_bypass,
  UpgradeDialogSource.gamification_premium_unlocked,
  UpgradeDialogSource.splash_page_annual_template,
  UpgradeDialogSource.all_tools,
  UpgradeDialogSource.end_of_year_sale_home_page,
  UpgradeDialogSource.end_of_year_sale_countdown_banner,
  UpgradeDialogSource.black_friday,
  UpgradeDialogSource.countdown_banner,
  UpgradeDialogSource.change_plan,
  UpgradeDialogSource.link_query,
  UpgradeDialogSource.navigation_bar,
  UpgradeDialogSource.top_bar_emails_sent_progress,
  UpgradeDialogSource.mobile_app_navigation_bar,
  UpgradeDialogSource.mobile_app_upgrade_page,
  UpgradeDialogSource.local_pitching_preview,
  UpgradeDialogSource.campaigns_preview,
  UpgradeDialogSource.campaigns_banner,
  UpgradeDialogSource.annual_plan_home_page,
  UpgradeDialogSource.template_evaluation_limit,
  UpgradeDialogSource.template_limit_increase_chip,
];

export const DisplayAllPlans: UpgradeDialogSource[] = [
  UpgradeDialogSource.onboarding_paywall,
  UpgradeDialogSource.mobile_app_settings,
];

export const MonthlyPlanOnly: (AddOnItemName | Plans | UpgradeDialogSource)[] =
  [AddOnItemName.partnership_data];

export const YearlyPlanOnly: UpgradeDialogSource[] = [
  UpgradeDialogSource.splash_page_annual_template,
  UpgradeDialogSource.free_template_with_annual_plan,
  UpgradeDialogSource.annual_plan_home_page,
  UpgradeDialogSource.mbg_countdown_banner,
  UpgradeDialogSource.mbg_mobile_promo_dialog,
  UpgradeDialogSource.annual_discount_banner,
];

export enum PlansMappingsKey {
  pro_only = "pro_only",
  va_only = "va_only",
  autopilot_only = "autopilot_only",
  pro_and_va = "pro_and_va",
  pro_and_autopilot = "pro_and_autopilot",
  all = "all",
}
const PlansMappings = {
  [SubscriptionTab.monthly]: {
    [PlansMappingsKey.pro_only]: [Plans.PRO],
    [PlansMappingsKey.va_only]: [Plans.VA],
    [PlansMappingsKey.autopilot_only]: [Plans.AUTOPILOT],
    [PlansMappingsKey.pro_and_va]: [Plans.PRO, Plans.VA],
    [PlansMappingsKey.pro_and_autopilot]: [Plans.PRO, Plans.AUTOPILOT],
    [PlansMappingsKey.all]: [Plans.FREE, Plans.PRO, Plans.VA],
  },
  [SubscriptionTab.yearly]: {
    [PlansMappingsKey.pro_only]: [Plans.PRO_YEARLY],
    [PlansMappingsKey.va_only]: [Plans.VA_YEARLY],
    [PlansMappingsKey.autopilot_only]: [Plans.AUTOPILOT_YEARLY],
    [PlansMappingsKey.pro_and_va]: [Plans.PRO_YEARLY, Plans.VA_YEARLY],
    [PlansMappingsKey.pro_and_autopilot]: [
      Plans.PRO_YEARLY,
      Plans.AUTOPILOT_YEARLY,
    ],
    [PlansMappingsKey.all]: [Plans.FREE, Plans.PRO_YEARLY, Plans.VA_YEARLY],
  },
};

export const BannerCouponSnippets = {
  [BannerCouponName.new_subscriber]: "New Subscriber Promo",
  [BannerCouponName.money_back_guarantee]: "Money-back guarantee",
};

export const getAllPlans = (
  tab: SubscriptionTab,
  upgradeDialogSource?: UpgradeDialogSource | null,
  showAutopilotPlan?: boolean,
) => {
  let mappingsKey = PlansMappingsKey.pro_only;
  if (!upgradeDialogSource || DisplayAllPlans.includes(upgradeDialogSource)) {
    mappingsKey = PlansMappingsKey.all;
  } else if (DisplayProAndVa.includes(upgradeDialogSource)) {
    mappingsKey = showAutopilotPlan
      ? PlansMappingsKey.pro_and_autopilot
      : PlansMappingsKey.pro_and_va;
  } else if (DisplayVaOnly.includes(upgradeDialogSource)) {
    mappingsKey = showAutopilotPlan
      ? PlansMappingsKey.autopilot_only
      : PlansMappingsKey.va_only;
  }
  const plans = PlansMappings[tab][mappingsKey];

  if (showAutopilotPlan && mappingsKey === PlansMappingsKey.all) {
    return plans.map((p: Plans) => {
      if (p === Plans.VA) return Plans.AUTOPILOT;
      if (p === Plans.VA_YEARLY) return Plans.AUTOPILOT_YEARLY;
      return p;
    });
  }

  return plans;
};

export const getPlanLimitText = (
  subscription: Subscription | null,
  plan: Plans,
  emailSents: number,
  plans: AllPlans | null,
  bannerPromoCodes: BannerPromoCode[] = [],
) => {
  if (!plans) return <></>;
  const status = subscription?.subscription?.status;
  const cancelAtText = subscription?.subscription?.cancelAt ? (
    <>
      Your subscription is set to cancel on{" "}
      <strong>
        {formatEpochTimestamp({
          epochTimestamp: subscription?.subscription?.cancelAt,
          addYear: true,
        })}
      </strong>{" "}
    </>
  ) : (
    <></>
  );

  if (
    (isOnFreePlan(subscription) && plan === Plans.FREE) ||
    (isCurrentPlan(subscription, plan) && status === SubscriptionType.ACTIVE)
  ) {
    return (
      <Typography component="div" sx={{ fontSize: 13 }}>
        You have sent{" "}
        <Box sx={{ color: "purple.main" }} component="span">
          <strong>
            {emailSents} out of{" "}
            {Math.max(emailSents, subscription?.monthlyLimit!)} emails
          </strong>
        </Box>{" "}
        this month.
        <br />
        {subscription?.subscription?.cancelAt ? (
          cancelAtText
        ) : (
          <>
            Your plan's limits will reset on{" "}
            {subscription &&
              formatEpochTimestamp({
                epochTimestamp: subscription.creditsCycle[1],
                addYear: true,
              })}
            .
            <br />
            <br />
          </>
        )}
      </Typography>
    );
  } else if (isCurrentPlan(subscription, plan) && status) {
    return SubscriptionMessages[status];
  } else if (
    (!subscription?.subscription?.planName ||
      subscription?.subscription?.planName === Plans.FREE) &&
    plan !== Plans.FREE
  ) {
    const isVaMoneyBack = bannerPromoCodes.some(
      (p) => p.couponName === BannerCouponName.money_back_guarantee,
    );
    const newSubscriberPromo = bannerPromoCodes.find(
      (p) => p.couponName === BannerCouponName.new_subscriber,
    );
    if (isVaMoneyBack && plan === Plans.VA_YEARLY) {
      return (
        <Typography sx={{ color: "green.main" }}>
          💸 If you don't earn more than what you paid in your first year of
          Virtual Assistant (yearly), <strong>we'll refund you</strong>
        </Typography>
      );
    } else if (
      !isVaMoneyBack &&
      newSubscriberPromo?.applicablePlans &&
      newSubscriberPromo?.applicablePlans?.includes(plan)
    ) {
      return (
        <Typography
          textAlign="center"
          sx={{
            textTransform: "uppercase",
            color: "black",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          💥 {BannerCouponSnippets[newSubscriberPromo?.couponName]}:{" "}
          <Box sx={{ color: "#39a47e" }} component="span">
            {newSubscriberPromo?.discountPercent}% Off
          </Box>{" "}
          <Box component="span" sx={{ color: "#d95e58" }}>
            Until {newSubscriberPromo?.dateEnding}
          </Box>
        </Typography>
      );
    } else {
      return <></>;
    }
  }
};
